import { zodResolver } from '@hookform/resolvers/zod';
import {
  BRAZILIAN_STATES,
  countryCodes,
  formatPhone,
  isValidCpf,
  normalizeInstagram,
  readPhone,
} from '@hue/contracts';
import {
  Button,
  Combobox,
  DatePicker,
  DocumentInput,
  FormErrorSummary,
  IconButton,
  Input,
  MaskedInput,
  Modal,
  PhoneInput,
  Select,
  StatusPill,
  Textarea,
  formatBrazilianDocument,
  useSnackbar,
  type CivilDate,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, KeyRound, MailPlus, Trash2 } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  clientsApi,
  getApiError,
  getApiErrorField,
  type Client,
  type ClientInput,
} from '../../lib/api.js';
import { FilterGroup, RequestAlert } from '../../app/data-view.js';
import { ClientConsents } from './client-consents.js';
import type { ClientCapabilities } from './company-clients.js';

const REFERRER_PAGE = 20;
const SEARCH_DELAY_MS = 250;

const clientSchema = z
  .object({
    kind: z.literal('PERSON'),
    displayName: z.string().trim().min(2, 'Informe o nome do cliente.').max(160),
    legalName: z.string().trim().max(200),
    document: z.string().trim().max(24),
    email: z.union([z.literal(''), z.email('Informe um e-mail válido.')]),
    phone: z.string().trim().max(24),
    birthDate: z.string().nullable(),
    notes: z.string().trim().max(1000),
    instagram: z.string().trim().max(120),
    addressCountry: z.string(),
    postalCode: z.string().trim().max(16),
    street: z.string().trim().max(200),
    number: z.string().trim().max(20),
    complement: z.string().trim().max(120),
    district: z.string().trim().max(120),
    city: z.string().trim().max(120),
    state: z.string().trim().max(60),
    referralMode: z.enum(['NONE', 'CLIENT', 'SOURCE']),
    referredById: z.string(),
    referredByName: z.string(),
    referralSource: z.string().trim().max(120),
    emergencyName: z.string().trim().max(160),
    emergencyRelationship: z.string().trim().max(60),
    emergencyPhone: z.string().trim().max(24),
  })
  .superRefine((value, context) => {
    const issue = (path: keyof typeof value, message: string) =>
      context.addIssue({ code: 'custom', path: [path], message });
    const document = value.document.replace(/\D/gu, '');
    if (document) {
      if (document.length !== 11) issue('document', 'O CPF tem 11 dígitos.');
      else if (!isValidCpf(document)) issue('document', 'CPF inválido: confira os dígitos.');
    }
    if (/\d/u.test(value.phone) && !readPhone(value.phone)) {
      issue(
        'phone',
        'Informe um telefone com DDD, ou com + e o código do país quando for de fora.',
      );
    }
    if (normalizeInstagram(value.instagram) === undefined) {
      issue('instagram', 'Só o usuário: letras, números, ponto ou sublinhado.');
    }
    if (value.addressCountry === 'BR') {
      const postalCode = value.postalCode.replace(/\D/gu, '');
      if (postalCode && postalCode.length !== 8) issue('postalCode', 'O CEP tem 8 dígitos.');
    }
    if ((value.emergencyRelationship || /\d/u.test(value.emergencyPhone)) && !value.emergencyName) {
      issue('emergencyName', 'Informe o nome do contato de emergência.');
    }
    if (/\d/u.test(value.emergencyPhone) && !readPhone(value.emergencyPhone)) {
      issue(
        'emergencyPhone',
        'Informe um telefone com DDD, ou com + e o código do país quando for de fora.',
      );
    }
    if (value.referralMode === 'CLIENT' && !value.referredById) {
      issue('referredById', 'Escolha o cliente que indicou.');
    }
    if (value.referralMode === 'SOURCE' && !value.referralSource) {
      issue('referralSource', 'Diga de onde o cliente veio.');
    }
  });
type ClientForm = z.infer<typeof clientSchema>;

const accessSchema = z.object({
  firstName: z.string().trim().min(2, 'Informe o primeiro nome.').max(120),
  lastName: z.string().trim().min(2, 'Informe o sobrenome.').max(120),
  email: z.email('Informe um e-mail válido.'),
  relationship: z.literal('CUSTOMER'),
});
type AccessForm = z.infer<typeof accessSchema>;

const countryNames = new Intl.DisplayNames(['pt-BR'], { type: 'region' });
type PostalCodeState = 'idle' | 'loading' | 'found' | 'missing' | 'down';

/// O cadastro do cliente e o acesso ao portal. O diálogo de cadastrar e a aba Cadastro da página do
/// cliente mostram a mesma ficha inteira — contato, endereço e indicação (ADR 0037): quem cadastra
/// não precisa voltar à página para completar. No diálogo, `frame` põe as ações no rodapé dele.
export function ClientProfile({
  client,
  capabilities,
  today,
  timeZone,
  onSaved,
  onCancel,
  onOpenClient,
  frame,
}: {
  client: Client | null;
  capabilities: ClientCapabilities;
  today: CivilDate;
  /// O fuso de quem lê, para as datas das autorizações.
  timeZone: string;
  onSaved: () => Promise<void>;
  onCancel?: (() => void) | undefined;
  /// Abre a página de outro cliente — quem indicou este.
  onOpenClient?: ((clientId: string) => void) | undefined;
  /// Envolve a ficha num diálogo, que recebe as ações para o rodapé fixo; sem ele, as ações
  /// fecham o formulário.
  frame?: ((body: ReactNode, actions: ReactNode) => ReactNode) | undefined;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [requestError, setRequestError] = useState<string>();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [revoking, setRevoking] = useState<string>();
  const [postalCodeState, setPostalCodeState] = useState<PostalCodeState>('idle');
  const formId = useId();
  const form = useForm<ClientForm>({
    resolver: zodResolver(clientSchema),
    defaultValues: client ? formOf(client) : emptyClient(),
  });
  const values = useWatch({ control: form.control }) as ClientForm;
  useEffect(() => {
    form.reset(client ? formOf(client) : emptyClient());
  }, [client, form]);

  const editable = client ? capabilities.canUpdate : capabilities.canCreate;
  const disabled = !editable;
  const errors = form.formState.errors;
  const brazil = values.addressCountry === 'BR';
  /// O número antigo que a migração não leu (sem "+") continua no campo, com o aviso para corrigir.
  const legacyPhone =
    client?.phone && !client.phone.startsWith('+') && !readPhone(client.phone)
      ? client.phone
      : null;
  const countries = useMemo(
    () =>
      countryCodes()
        .map((code) => ({ value: code, label: countryNames.of(code) ?? code }))
        .sort((a, b) =>
          a.value === 'BR' ? -1 : b.value === 'BR' ? 1 : a.label.localeCompare(b.label, 'pt-BR'),
        ),
    [],
  );

  function set<K extends keyof ClientForm>(name: K, value: ClientForm[K]) {
    form.setValue(name, value as never, { shouldValidate: form.formState.isSubmitted });
  }

  async function lookupPostalCode(text: string) {
    const digits = text.replace(/\D/gu, '');
    if (digits.length !== 8) return;
    setPostalCodeState('loading');
    try {
      const address = await clientsApi.findPostalCode(digits);
      if (form.getValues('postalCode').replace(/\D/gu, '') !== digits) return;
      if (address.street) set('street', address.street);
      if (address.district) set('district', address.district);
      set('city', address.city);
      set('state', address.state);
      setPostalCodeState('found');
      form.setFocus('number');
    } catch (cause) {
      const status = (cause as { response?: { status?: number } }).response?.status;
      setPostalCodeState(status === 404 ? 'missing' : 'down');
    }
  }

  async function save(data: ClientForm) {
    setRequestError(undefined);
    const address = {
      country: data.addressCountry,
      postalCode: data.postalCode || null,
      street: data.street || null,
      number: data.number || null,
      complement: data.complement || null,
      district: data.district || null,
      city: data.city || null,
      state: data.state || null,
    };
    const hasAddress = Object.entries(address).some(([key, part]) => key !== 'country' && part);
    const payload: ClientInput = {
      kind: data.kind,
      displayName: data.displayName,
      legalName: data.legalName || null,
      document: data.document || null,
      email: data.email || null,
      phone: data.phone || null,
      birthDate: data.birthDate,
      notes: data.notes || null,
      instagram: normalizeInstagram(data.instagram) ?? null,
      address: hasAddress ? address : null,
      referredByClientId: data.referralMode === 'CLIENT' ? data.referredById : null,
      referralSource: data.referralMode === 'SOURCE' ? data.referralSource : null,
      emergencyContact: data.emergencyName
        ? {
            name: data.emergencyName,
            relationship: data.emergencyRelationship || null,
            phone: /\d/u.test(data.emergencyPhone) ? data.emergencyPhone : null,
          }
        : null,
    };
    try {
      if (client) await clientsApi.update(client.id, payload);
      else await clientsApi.create(payload);
      await onSaved();
    } catch (cause) {
      const field = {
        document: 'document',
        referredByClientId: 'referredById',
        'emergencyContact.name': 'emergencyName',
        'emergencyContact.phone': 'emergencyPhone',
      } as const;
      const target = field[getApiErrorField(cause) as keyof typeof field];
      if (target) form.setError(target, { message: getApiError(cause) });
      else setRequestError(getApiError(cause));
    }
  }

  async function revoke(accessId: string) {
    if (!client) return;
    setRevoking(accessId);
    try {
      await clientsApi.revokeAccess(client.id, accessId);
      await queryClient.invalidateQueries({ queryKey: ['company', 'clients'] });
      snackbar.success('Acesso revogado.');
    } catch (cause) {
      setRequestError(getApiError(cause));
    } finally {
      setRevoking(undefined);
    }
  }

  const instagram = normalizeInstagram(values.instagram);
  const postalCodeHint = {
    idle: brazil ? 'Com o CEP, o endereço vem preenchido.' : undefined,
    loading: 'Buscando o endereço…',
    found: 'Endereço preenchido pelo CEP. Confira e informe o número.',
    missing: 'CEP não encontrado. Preencha o endereço à mão.',
    down: 'Não foi possível consultar o CEP agora. Preencha à mão.',
  }[postalCodeState];

  const actions = editable ? (
    <>
      <FormErrorSummary
        form={formId}
        className={frame ? 'basis-full' : 'basis-full sm:basis-auto sm:flex-1'}
      />
      {onCancel ? (
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
      ) : null}
      <Button type="submit" form={formId} loading={form.formState.isSubmitting}>
        {client ? 'Salvar cliente' : 'Cadastrar cliente'}
      </Button>
    </>
  ) : null;

  const body = (
    <>
      <form id={formId} noValidate onSubmit={(event) => void form.handleSubmit(save)(event)}>
        <div className="grid gap-7">
          <FormSection title="Identificação">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Nome de exibição"
                error={errors.displayName?.message}
                disabled={disabled}
                {...form.register('displayName')}
              />
              <DatePicker
                label="Data de nascimento"
                value={values.birthDate}
                onValueChange={(value) => set('birthDate', value)}
                today={today}
                error={errors.birthDate?.message}
                disabled={disabled}
              />
              <DocumentInput
                kind="CPF"
                value={values.document}
                onValueChange={(text) => set('document', text)}
                error={errors.document?.message}
                disabled={disabled}
              />
            </div>
          </FormSection>

          <FormSection title="Contato">
            {legacyPhone && values.phone === legacyPhone ? (
              <p className="rounded-xl border border-warning/30 bg-warning/10 p-3 text-xs text-content-secondary">
                O telefone gravado antes da máscara não foi reconhecido:{' '}
                <span className="font-semibold tabular-nums">{legacyPhone}</span>. Digite de novo
                com DDD, ou com + e o código do país.
              </p>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="E-mail"
                type="email"
                autoComplete="email"
                error={errors.email?.message}
                disabled={disabled}
                {...form.register('email')}
              />
              <PhoneInput
                label="Telefone"
                value={values.phone}
                onValueChange={(text) => set('phone', text)}
                error={errors.phone?.message}
                disabled={disabled}
              />
              <Input
                label="Instagram"
                placeholder="usuario"
                leftIcon={<span className="text-sm font-semibold">@</span>}
                error={errors.instagram?.message}
                disabled={disabled}
                {...form.register('instagram')}
                action={
                  instagram ? (
                    <a
                      href={`https://instagram.com/${instagram}`}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Abrir perfil no Instagram"
                      className="grid size-12 place-items-center rounded-xl border border-surface-border text-content-muted transition hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                    >
                      <ExternalLink className="size-4" />
                    </a>
                  ) : undefined
                }
              />
            </div>
          </FormSection>

          <FormSection
            title="Contato de emergência"
            hint="Opcional. Aparece no PDF da ficha e do prontuário."
          >
            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,12rem)]">
              <Input
                label="Nome"
                error={errors.emergencyName?.message}
                disabled={disabled}
                {...form.register('emergencyName')}
              />
              <Input
                label="Parentesco"
                placeholder="Mãe, irmão, amiga…"
                error={errors.emergencyRelationship?.message}
                disabled={disabled}
                {...form.register('emergencyRelationship')}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <PhoneInput
                label="Telefone do contato"
                value={values.emergencyPhone}
                onValueChange={(text) => set('emergencyPhone', text)}
                error={errors.emergencyPhone?.message}
                disabled={disabled}
              />
            </div>
          </FormSection>

          <FormSection
            title="Endereço"
            hint="Opcional agora; a nota fiscal vai exigir quando existir."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Combobox
                label="País"
                placeholder="Buscar país"
                selectedLabel={countryNames.of(values.addressCountry) ?? values.addressCountry}
                options={countries}
                onSelect={(choice) => {
                  const country = (choice as { value: string }).value;
                  if (country === values.addressCountry) return;
                  set('addressCountry', country);
                  set('postalCode', '');
                  set('state', '');
                  setPostalCodeState('idle');
                }}
                onClear={() => set('addressCountry', 'BR')}
                emptyLabel="Nenhum país encontrado"
                disabled={disabled}
              />
              {brazil ? (
                <MaskedInput
                  label="CEP"
                  inputMode="numeric"
                  placeholder="00000-000"
                  value={values.postalCode}
                  format={(digits) => digits.slice(0, 8).replace(/^(\d{5})(\d)/u, '$1-$2')}
                  onValueChange={(text) => {
                    set('postalCode', text);
                    setPostalCodeState('idle');
                    void lookupPostalCode(text);
                  }}
                  error={errors.postalCode?.message}
                  hint={postalCodeHint}
                  hintTone={
                    postalCodeState === 'found'
                      ? 'positive'
                      : postalCodeState === 'missing' || postalCodeState === 'down'
                        ? 'warning'
                        : 'muted'
                  }
                  disabled={disabled}
                />
              ) : (
                <Input
                  label="Código postal"
                  error={errors.postalCode?.message}
                  disabled={disabled}
                  {...form.register('postalCode')}
                />
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_8rem_minmax(0,12rem)]">
              <Input label="Rua" disabled={disabled} {...form.register('street')} />
              <Input label="Número" disabled={disabled} {...form.register('number')} />
              <Input
                label="Complemento"
                placeholder="Apto, sala…"
                disabled={disabled}
                {...form.register('complement')}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_9rem]">
              <Input label="Bairro" disabled={disabled} {...form.register('district')} />
              <Input label="Cidade" disabled={disabled} {...form.register('city')} />
              {brazil ? (
                <Select
                  label="UF"
                  value={values.state}
                  options={[
                    { value: '', label: 'UF' },
                    ...BRAZILIAN_STATES.map((state) => ({ value: state, label: state })),
                  ]}
                  onValueChange={(value) => set('state', value)}
                  disabled={disabled}
                />
              ) : (
                <Input
                  label="Estado ou província"
                  disabled={disabled}
                  {...form.register('state')}
                />
              )}
            </div>
          </FormSection>

          <FormSection title="Indicação" hint="Quem ou o que trouxe o cliente.">
            {editable ? (
              <FilterGroup
                label="Tipo de indicação"
                description="Origem do cliente"
                options={[
                  { value: 'NONE', label: 'Não informada' },
                  { value: 'CLIENT', label: 'Outro cliente' },
                  { value: 'SOURCE', label: 'Outra origem' },
                ]}
                value={values.referralMode}
                onChange={(mode) => set('referralMode', mode)}
              />
            ) : null}
            {values.referralMode === 'CLIENT' ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <ReferrerField
                  selfId={client?.id}
                  selectedLabel={values.referredByName}
                  disabled={disabled}
                  error={errors.referredById?.message}
                  onSelect={(id, name) => {
                    set('referredById', id);
                    set('referredByName', name);
                  }}
                />
                {values.referredById && onOpenClient ? (
                  <div className="self-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenClient(values.referredById)}
                    >
                      Abrir {values.referredByName}
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : values.referralMode === 'SOURCE' ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Origem"
                  placeholder="Google, feira, Dra. Paula…"
                  error={errors.referralSource?.message}
                  disabled={disabled}
                  {...form.register('referralSource')}
                />
              </div>
            ) : !editable ? (
              <p className="text-sm text-content-muted">Não informada.</p>
            ) : null}
          </FormSection>

          <FormSection>
            <Textarea
              label="Observações"
              rows={3}
              error={errors.notes?.message}
              disabled={disabled}
              {...form.register('notes')}
            />
          </FormSection>
        </div>
        {requestError ? <RequestAlert message={requestError} /> : null}
      </form>
      {actions && !frame ? (
        <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-surface-border pt-5">
          {actions}
        </div>
      ) : null}
      {client ? (
        <ClientConsents client={client} canUpdate={capabilities.canUpdate} timeZone={timeZone} />
      ) : null}
      {client && capabilities.canManageAccess ? (
        <div className="mt-7 border-t border-surface-border pt-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">Acesso ao portal</h3>
              <p className="mt-1 text-xs text-content-muted">
                O convite associa uma identidade global sem compartilhar o cadastro.
              </p>
            </div>
            <Button variant="outline" onClick={() => setInviteOpen(true)}>
              <MailPlus className="size-4" />
              Convidar
            </Button>
          </div>
          {client.accesses.length ? (
            <ul className="mt-4 space-y-2">
              {client.accesses.map((access) => (
                <li
                  key={access.id}
                  className="flex items-center gap-3 rounded-xl border border-surface-border p-3"
                >
                  <KeyRound className="size-4 shrink-0 text-brand-400" />
                  <div className="min-w-0 flex-1">
                    <strong className="block truncate text-xs">{access.displayName}</strong>
                    <span className="block truncate text-xs text-content-muted">
                      {access.email} · Cliente
                    </span>
                  </div>
                  <StatusPill tone={access.status === 'ACTIVE' ? 'positive' : 'progress'}>
                    {access.status === 'ACTIVE' ? 'Ativo' : 'Convidado'}
                  </StatusPill>
                  <IconButton
                    label="Revogar acesso"
                    tone="danger"
                    disabled={revoking === access.id}
                    icon={<Trash2 className="size-4" />}
                    onClick={() => void revoke(access.id)}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 rounded-xl border border-dashed border-surface-border p-4 text-center text-xs text-content-muted">
              Nenhuma identidade vinculada.
            </p>
          )}
        </div>
      ) : null}
      {client ? (
        <AccessDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          client={client}
          onInvited={async () => {
            await queryClient.invalidateQueries({ queryKey: ['company', 'clients'] });
            setInviteOpen(false);
            snackbar.success('Convite enviado sem revelar se a pessoa já possuía conta.');
          }}
        />
      ) : null}
    </>
  );
  return frame ? frame(body, actions) : body;
}

function AccessDialog({
  open,
  onOpenChange,
  client,
  onInvited,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client;
  onInvited: () => Promise<void>;
}) {
  const [requestError, setRequestError] = useState<string>();
  const form = useForm<AccessForm>({
    resolver: zodResolver(accessSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      relationship: 'CUSTOMER',
    },
  });
  async function invite(data: AccessForm) {
    setRequestError(undefined);
    try {
      await clientsApi.inviteAccess(client.id, data);
      await onInvited();
      form.reset();
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Convidar para o portal"
      description="A resposta é sempre neutra sobre a existência de uma conta global."
    >
      <form noValidate onSubmit={(event) => void form.handleSubmit(invite)(event)}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Primeiro nome"
            error={form.formState.errors.firstName?.message}
            {...form.register('firstName')}
          />
          <Input
            label="Sobrenome"
            error={form.formState.errors.lastName?.message}
            {...form.register('lastName')}
          />
          <Input
            className="sm:col-span-2"
            label="E-mail"
            type="email"
            autoComplete="email"
            error={form.formState.errors.email?.message}
            {...form.register('email')}
          />
        </div>
        {requestError ? <RequestAlert message={requestError} /> : null}
        <div className="mt-6 flex justify-end gap-3 border-t border-surface-border pt-5">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button loading={form.formState.isSubmitting}>Enviar convite</Button>
        </div>
      </form>
    </Modal>
  );
}

/// Bloco do formulário com título.
function FormSection({
  title,
  hint,
  children,
}: {
  /// Sem título quando o próprio campo já diz o que é — "Observações" sobre "Observações".
  title?: string | undefined;
  hint?: string | undefined;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-4 border-t border-surface-border pt-6 first:border-t-0 first:pt-0">
      {title ? (
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          {hint ? <p className="mt-1 text-xs text-content-muted">{hint}</p> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/// Quem indicou: outro cliente desta empresa, buscado no servidor — a lista não tem teto. O
/// próprio cliente aparece, mas não se escolhe.
function ReferrerField({
  selfId,
  selectedLabel,
  disabled,
  error,
  onSelect,
}: {
  /// Ausente no cadastro: o cliente ainda não existe para se excluir da lista.
  selfId?: string | undefined;
  selectedLabel: string;
  disabled: boolean;
  error?: string | undefined;
  onSelect: (clientId: string, displayName: string) => void;
}) {
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [text]);
  const result = useQuery({
    queryKey: ['company', 'clients', 'referrer-search', query],
    queryFn: () =>
      clientsApi.list({
        ...(query ? { search: query } : {}),
        activity: 'ALL',
        kind: 'ALL',
        access: 'ALL',
        sort: 'displayName',
        direction: 'asc',
        limit: REFERRER_PAGE,
        offset: 0,
      }),
    enabled: !disabled,
    placeholderData: keepPreviousData,
    retry: false,
  });
  const clients = (result.data?.clients ?? []).filter((client) => client.id !== selfId);
  return (
    <Combobox
      label="Indicado por"
      placeholder="Buscar cliente por nome, telefone ou documento"
      selectedLabel={selectedLabel}
      options={clients.map((client) => ({
        value: client.id,
        label: client.displayName,
        description: formatPhone(client.phone) ?? undefined,
      }))}
      onSelect={(choice) => {
        const { value } = choice as { value: string };
        onSelect(value, clients.find((item) => item.id === value)!.displayName);
      }}
      onQueryChange={setText}
      loading={result.isFetching}
      onClear={() => onSelect('', '')}
      emptyLabel="Nenhum cliente encontrado"
      disabled={disabled}
      error={error}
    />
  );
}

function emptyClient(): ClientForm {
  return {
    kind: 'PERSON',
    displayName: '',
    legalName: '',
    document: '',
    email: '',
    phone: '',
    birthDate: null,
    notes: '',
    instagram: '',
    addressCountry: 'BR',
    postalCode: '',
    street: '',
    number: '',
    complement: '',
    district: '',
    city: '',
    state: '',
    referralMode: 'NONE',
    referredById: '',
    referredByName: '',
    referralSource: '',
    emergencyName: '',
    emergencyRelationship: '',
    emergencyPhone: '',
  };
}

function formOf(client: Client): ClientForm {
  const address = client.address;
  const country = address?.country ?? 'BR';
  return {
    kind: client.kind,
    displayName: client.displayName,
    legalName: client.legalName ?? '',
    document: client.document ? formatBrazilianDocument(client.document, 'CPF') : '',
    email: client.email ?? '',
    phone: formatPhone(client.phone) ?? '',
    birthDate: client.birthDate,
    notes: client.notes ?? '',
    instagram: client.instagram ?? '',
    addressCountry: country,
    postalCode:
      country === 'BR' && address?.postalCode
        ? address.postalCode.replace(/^(\d{5})(\d{3})$/u, '$1-$2')
        : (address?.postalCode ?? ''),
    street: address?.street ?? '',
    number: address?.number ?? '',
    complement: address?.complement ?? '',
    district: address?.district ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    referralMode: client.referredBy ? 'CLIENT' : client.referralSource ? 'SOURCE' : 'NONE',
    referredById: client.referredBy?.id ?? '',
    referredByName: client.referredBy?.displayName ?? '',
    referralSource: client.referralSource ?? '',
    emergencyName: client.emergencyContact?.name ?? '',
    emergencyRelationship: client.emergencyContact?.relationship ?? '',
    emergencyPhone: formatPhone(client.emergencyContact?.phone ?? null) ?? '',
  };
}
